<script setup lang="ts">
const FACEBOOK_ICON = 'https://resources1.interface003.com/web30/2026/07/14/318f97e9-ec23-41fc-bd14-22be9b1a8e5c.png'
const WECHAT_ICON = 'https://resources1.interface003.com/web30/2026/07/14/17c4d6a7-ed2c-4c93-89d6-4af68ad45ec0.png'
const X_ICON = 'https://resources1.interface003.com/web30/2026/07/14/dd8abd47-e90a-40dd-81b1-9d4c4b63efea.png'

interface Props {
  title?: string
  url?: string
  label?: string
  wechatCaption?: string
  facebookAppId?: string
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  url: '',
  label: '',
  wechatCaption: '',
  facebookAppId: '1455456457932751',
})

const route = useRoute()
const requestUrl = useRequestURL()
const { locale, t } = useAppI18n()

const rootRef = ref<HTMLElement | null>(null)
const isWechatShareOpen = ref(false)

/**
 * 判断当前语言是否为中文，用于兼容二维码文案回退。
 */
const isChineseLocale = computed(() => locale === 'zh-cn' || locale === 'zh')

/**
 * 生成微信分享弹层的提示文案。
 */
const wechatShareCaption = computed(
  () => props.wechatCaption || (isChineseLocale.value ? t('84045') : 'Share on WeChat'),
)

/**
 * 生成当前页面的分享地址，优先使用外部传入值。
 */
const shareUrl = computed(() => {
  const customUrl = props.url.trim()
  if (customUrl) return customUrl

  if (import.meta.client) {
    return window.location.href
  }

  return new URL(route.fullPath || '/', requestUrl.origin).toString()
})

const xLang = computed(() => (locale === 'zh' ? 'zh-tw' : locale))
/**
 * 生成 X 分享链接。
 */
const xShareUrl = computed(
  () =>
    `https://x.com/intent/tweet?text=${encodeURIComponent(props.title)}&url=${encodeURIComponent(shareUrl.value)}&lang=${xLang.value}`,
)

/**
 * 生成 Facebook 分享链接。
 */
const facebookShareUrl = computed(
  () =>
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl.value)}&app_id=${props.facebookAppId}`,
)

/**
 * 打开微信二维码弹层。
 */
const openWechatShare = () => {
  isWechatShareOpen.value = true
}

/**
 * 关闭微信二维码弹层。
 */
const closeWechatShare = () => {
  isWechatShareOpen.value = false
}

/**
 * 切换微信二维码弹层显隐。
 */
const toggleWechatShare = () => {
  isWechatShareOpen.value = !isWechatShareOpen.value
}

/**
 * 处理点击空白区域时关闭微信二维码弹层。
 * @param event 鼠标点击事件
 */
const handleDocumentClick = (event: MouseEvent) => {
  const target = event.target as Node | null
  if (!target || rootRef.value?.contains(target)) return
  closeWechatShare()
}

onMounted(() => {
  if (!import.meta.client) return
  document.addEventListener('click', handleDocumentClick)
})

onBeforeUnmount(() => {
  if (!import.meta.client) return
  document.removeEventListener('click', handleDocumentClick)
})
</script>

<template>
  <div ref="rootRef" class="relative flex items-center gap-[4px]">
    <div class="relative inline-flex items-center">
      <button
        type="button"
        class="inline-flex size-[20px] cursor-pointer items-center justify-center overflow-hidden rounded-full border-none bg-transparent p-0 transition-opacity hover:opacity-90"
        aria-label="Share on WeChat"
        @click.stop="toggleWechatShare"
        @mouseenter="openWechatShare"
        @mouseleave="closeWechatShare"
      >
        <CommonImage :src="WECHAT_ICON" alt="WeChat" class="size-[20px] rounded-full object-cover" loading="eager"/>
      </button>

      <div
        v-show="isWechatShareOpen"
        class="absolute top-[26px] right-0 z-[2] flex w-[140px] flex-col items-center gap-[8px] rounded-[8px] bg-white p-[16px] shadow-[0_8px_24px_rgba(0,0,0,0.1),0_16px_48px_rgba(0,0,0,0.1)] rtl:right-auto rtl:left-0"
        @mouseenter="openWechatShare"
        @mouseleave="closeWechatShare"
        @click.stop
      >
        <CommonQrCode class="size-[108px] overflow-hidden" :text="shareUrl" :size="108" />
        <span class="h-[22px] text-[14px] leading-[22px] font-medium whitespace-nowrap text-[#292A2E]">
          {{ wechatShareCaption }}
        </span>
      </div>
    </div>
    <NuxtLink
      class="inline-flex size-[20px] cursor-pointer items-center justify-center overflow-hidden rounded-full transition-opacity hover:opacity-90"
      :to="xShareUrl"
      external
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Share on X"
    >
      <CommonImage :src="X_ICON" alt="X" class="size-[20px] rounded-full object-cover" loading="eager"/>
    </NuxtLink>

    <NuxtLink
      class="inline-flex size-[20px] cursor-pointer items-center justify-center overflow-hidden rounded-full transition-opacity hover:opacity-90"
      :to="facebookShareUrl"
      external
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Share on Facebook"
    >
      <CommonImage :src="FACEBOOK_ICON" alt="Facebook" class="size-[20px] rounded-full object-cover" loading="eager"/>
    </NuxtLink>
  </div>
</template>
